import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Row, Col, Select, TimePicker } from 'antd';
import useApi from '../../hooks/useApi';
import useUserType from '../../hooks/useUserType';
import moment from 'moment';

const ModalAdd = ({ visible, onCancel, onConfirm }) => {
  const [form] = Form.useForm();
  const [departamentos, setDepartamentos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [tipoUsuario, setTipoUsuario] = useState('usuario');

  const userType = useUserType();
  const options = userType === 'administrador' 
  ? [
      { value: 'administrador', label: 'Administrador' },
      { value: 'gerenciador', label: 'Gerenciador' },
      { value: 'usuario', label: 'Usuário' }
    ]
  : [
      { value: 'usuario', label: 'Usuário' }
    ];

  const { callApi: addUsuario } = useApi('https://vision.alfama.com/api/add/usuarios', {
    method: 'POST',
    includeToken: true,
    successMessage: 'Usuário adicionado com sucesso!',
    errorMessage: 'Erro ao adicionar usuário'
  });

  const { callApi: viewDepartamentos } = useApi('https://vision.alfama.com/api/view/departamentos', { 
    includeToken: true,
  });

  const { callApi: viewGrupos } = useApi('https://vision.alfama.com/api/view/grupos_permissao/ativos', { 
    includeToken: true,
  });

  useEffect(() => {
    const fetchDepartamentos = async () => {
      try {
        const response = await viewDepartamentos();
        if (response && Array.isArray(response)) {
          const formattedDepartamentos = response.map(departamento => ({
            value: departamento.departamento_id,
            label: departamento.nome_departamento,
            // Adicionando campo para pesquisa
            searchText: departamento.nome_departamento.toLowerCase()
          }));
          setDepartamentos(formattedDepartamentos);
        }
      } catch (error) {
        console.error('Erro ao buscar departamentos:', error);
      }
    };

    fetchDepartamentos();
  }, []);

  useEffect(() => {
    const fetchGrupos = async () => {
      try {
        const response = await viewGrupos();
        if (response && Array.isArray(response)) {
          const formattedGrupos = response.map(grupos => ({
            value: grupos.grupo_id,
            label: `${grupos.nome_grupo}`,
            // Adicionando campo para pesquisa
            searchText: `${grupos.nome_grupo}`.toLowerCase()
          }));
          setGrupos(formattedGrupos);
        }
      } catch (error) {
        console.error('Erro ao buscar grupos:', error);
      }
    };

    fetchGrupos();
  }, []);

  const handleConfirm = async () => {
    try {
      const values = await form.validateFields();
      if (values.horario_entrada) {
        values.horario_entrada = values.horario_entrada.format('HH:mm:ss');
      }
      if (values.horario_saida) {
        values.horario_saida = values.horario_saida.format('HH:mm:ss');
      }
      
      if (values.tipo_usuario === 'administrador') {
        values.permissoes = [];
      }

      await addUsuario(values);
      form.resetFields();
      onConfirm(true);
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error('Erro ao adicionar usuário:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleTipoUsuarioChange = (value) => {
    setTipoUsuario(value);
    if (value === 'administrador') {
      form.setFieldValue('permissoes', []);
    }
  };

  // Função de filtro personalizada para os Selects
  const filterOption = (input, option) => {
    return option.searchText.includes(input.toLowerCase());
  };

  return (
    <Modal
      title="Adicionar Usuário"
      open={visible}
      onCancel={handleCancel}
      onOk={handleConfirm}
      okText="Adicionar"
      cancelText="Cancelar"
      maskClosable={false}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          tipo_usuario: 'usuario',
          horario_entrada: moment('00:00:01', 'HH:mm:ss'),
          horario_saida: moment('23:59:59', 'HH:mm:ss'),
          permissoes: []
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="nome"
              label="Nome Completo"
              rules={[{ required: true, message: 'Por favor, insira o nome completo' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Por favor, insira o email' },
                { type: 'email', message: 'Por favor, insira um email válido' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="departamento"
              label="Departamento"
              rules={[{ required: true, message: 'Por favor, selecione um departamento' }]}
            >
              <Select
                showSearch
                placeholder="Selecione um departamento"
                options={departamentos}
                loading={departamentos.length === 0}
                filterOption={filterOption}
                optionFilterProp="children"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="tipo_usuario"
              label="Tipo de Usuário"
              rules={[{ required: true }]}
            >
              <Select
                onChange={handleTipoUsuarioChange}
                options={options}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="permissoes"
              label="Grupos de Permissão"
              rules={[
                {
                  required: tipoUsuario !== 'administrador',
                  message: 'Por favor, selecione ao menos uma permissão'
                }
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder={
                  tipoUsuario === 'administrador'
                    ? 'Administrador tem acesso a todas as permissões'
                    : 'Selecione as permissões'
                }
                allowClear
                style={{ width: '100%' }}
                options={grupos}
                loading={grupos.length === 0}
                disabled={tipoUsuario === 'administrador'}
                filterOption={filterOption}
                optionFilterProp="children"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="nome_usuario"
              label="Nome de Usuário"
              rules={[{ required: true, message: 'Por favor, insira o nome de usuário' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="senha"
              label="Senha"
              rules={[{ required: true, message: 'Por favor, insira a senha' }]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="horario_entrada"
              label="Horário de Entrada"
            >
              <TimePicker format="HH:mm:ss" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="horario_saida"
              label="Horário de Saída"
            >
              <TimePicker format="HH:mm:ss" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalAdd;